<template>
  <div class="clinic-config-tab tab-scroll-owner" v-loader.opacity.tab="loading">
    <div class="action-buttons rounded text-end">
      <button class="btn btn-outline-success" type="button" name="button" data-test="btnSave" @click="doSave">
        <i class="fa fa-check"></i>
        <span>{{ $t("save") }}</span>
      </button>
    </div>

    <div class="alert" :class="alert.class" role="alert" v-if="alert.message">
      <span v-html="alert.message"></span>
      <button type="button" class="btn-close" aria-label="Close" @click="alert.message = null"></button>
    </div>

    <div class="row">
      <div class="col-md-6">
        <h3 class="sub-title">{{ $t('clinicConfig.preAssessment') }}</h3>

        <div class="form-floating v-select-floating mb-3">
          <v-select
            class="v-select form"
            id="selectedTrigger"
            :placeholder="$t('clinicConfig.trigger')"
            v-model="preAnalysisOptions.method"
            data-test="vSelectTrigger"
            :reduce="x => x.value"
            :options="triggerOptions"/>
          <label for="selectedTrigger">{{ $t('clinicConfig.trigger') }}</label>
        </div>

        <div class="form-floating v-select-floating mb-3" v-if="preAnalysisOptions.method == 'threshold'">
          <v-select
            class="v-select form"
            id="ruleSelected"
            :placeholder="$t('clinicConfig.rule')"
            v-model="preAnalysisOptions.rule"
            data-test="vSelectRules"
            :reduce="x => x.value"
            :options="ruleOptions"/>
          <label for="ruleSelected">{{ $t('clinicConfig.rule') }}</label>
        </div>

        <div class="form-floating v-select-floating mb-3" v-if="preAnalysisOptions.method == 'mcRAS'">
          <v-select
            multiple
            class="v-select form grown"
            id="mcRASSelected"
            :placeholder="$t('clinicConfig.mcRAS')"
            v-model="preAnalysisOptions.classes"
            data-test="vSelectMcRAS"
            :reduce="x => x.value"
            :options="mcRASOptions"/>
          <label for="mcRASSelected">{{ $t('clinicConfig.mcRAS') }}</label>
        </div>

        <div class="form-floating mb-3" v-if="preAnalysisOptions.method">
          <input class="form-control" type="text" id="alteredEye" v-model="preAnalysisOptions.threshold" data-test="inputAlteredEye" :placeholder="$t('clinicConfig.threshold')" :disabled="disabled"/>
          <label for="alteredEye" class="">{{ $t('clinicConfig.threshold') }}</label>
        </div>

        <hr/>

        <div class="form-floating v-select-floating mb-3">
          <v-select
            class="v-select form"
            id="forwardedSelected"
            :placeholder="$t('clinicConfig.referred')"
            v-model="preAnalysisOptions.autoReferrerMode"
            data-test="vSelectReferred"
            :reduce="x => x.value"
            :options="forwardedOptions"/>
          <label for="forwardedSelected" class="">{{ $t('clinicConfig.referred') }}</label>
        </div>
        <div class="form-floating v-select-floating mb-3">
          <v-select
            class="v-select form"
            id="nextScreen"
            :placeholder="$t('clinicConfig.screenFlow')"
            v-model="preAnalysisOptions.nextScreen"
            data-test="vSelectFlow"
            :reduce="x => x.value"
            :options="flowOptions"/>
          <label for="nextScreen" class="">{{ $t('clinicConfig.screenFlow') }}</label>
        </div>
        <div class="form-floating v-select-floating mb-3">
          <v-select
            class="v-select form"
            id="alertMode"
            :placeholder="$t('clinicConfig.alert')"
            v-model="preAnalysisOptions.alertMode"
            data-test="vSelectAlert"
            :reduce="x => x.value"
            :options="alertOptions"/>
          <label for="alertMode" class="">{{ $t('clinicConfig.alert') }}</label>
        </div>
      </div>
      <div class="col-md-6">
        <h3 class="sub-title flex-between">
          {{ $t('clinicConfig.externalNotification') }}
          <button class="btn btn-outline-primary btn-sm" type="button" name="button" data-test="btnOpenModal" @click="openModalNotification">
            <i class="fa fa-plus"></i>
          </button>
        </h3>
        <template v-if="notifications.length > 0">
          <BaseAccordion v-for="(item, index) in notifications" :key="index">
            <template v-slot:header>
              <h4>{{ item.name }}</h4>
            </template>
            <template v-slot:content>
              <div class="action-buttons rounded text-end">

                <button class="btn btn-outline-danger" type="button" name="button" data-test="btnRemove" @click="confirmNotificationRemoval(item)">
                  <i class="fa fa-ban"></i>
                  <span>{{ $t('remove') }}</span>
                </button>
                <button class="btn btn-outline-warning" type="button" name="button" data-test="btnEdit" @click="openModalNotification(item)">
                  <i class="fa fa-pencil"></i>
                  <span>{{ $t('edit') }}</span>
                </button>
              </div>
              <p>
                {{ $t('clinicConfig.type') }}:
                <span>{{ item.type }}</span>
              </p>
              <p>
                {{ $t('clinicConfig.trigger') }}:
                <span class="no-wrap">{{ item.trigger.type }} | {{ item.trigger.rule ? item.trigger.rule : item.trigger.classes.join(',') }} | {{ item.trigger.value }}</span>
              </p>

              <hr/>

              <BaseTableOld class="white-table small" v-if="item.table" :tabledata="item.table" :key="index+1">
                <template #actiontwo="{item}">
                  <button class="btn btn-danger btn-sm" type="button" name="button" data-title="Delete" data-test="btnDelete" @click.stop="$emit('open-danger', item.id)">
                    <i class="fa fa-trash-o"></i>
                  </button>
                </template>
              </BaseTableOld>
            </template>
          </BaseAccordion>
        </template>
      </div>
    </div>

    <teleport to="body">
      <BaseGenericModal :modal_id="'modalClinicConfigGeneric'" ref="modalGeneric"/>

      <NewNotificationModal
        v-if="showModalNotification && clinic.id"
        :clinic="clinic"
        :selected_notification="selectedNotification"
        @close="closeModalNewNotification"/>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
  .accordion {
    .accordion-content {
      p {
        font-weight:              bold;
        display:                  inline-block;
        padding:                  5px 10px;
        margin-bottom:            0;
        span {
          font-weight:            normal;
          margin-left:            5px;
          float:                  right;
        }
      }
    }
    .action-buttons .btn {
      font-size:                  9px;
      padding:                    5px 10px 3px;
    }
    .accordion-header {
      h4 {
        font-size:                18px;
        display:                  inline-block;
        margin:                   0;
        line-height:              1.5;
        // vertical-align:           middle;
      }
    }
  }

  .col-md-6 > .sub-title .btn {
    font-size:                    12px;
    margin-bottom:                2px;
    padding:                      3px 10px;
  }
</style>

<script src="./ClinicConfigurationTab.js"></script>
